import { cn } from '@/lib/cn';
import {
  For,
  type JSX,
  createContext,
  createSignal,
  onCleanup,
  onMount,
  useContext,
} from 'solid-js';
import { Portal } from 'solid-js/web';
import { Icon, type IconName } from './Icon';

type ToastTone = 'success' | 'error' | 'info';

interface ToastMessage {
  id: string;
  tone: ToastTone;
  message: string;
}

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONE_CLASSES: Record<ToastTone, string> = {
  success: 'bg-green-500 text-white',
  error: 'bg-red-500 text-white',
  info: 'bg-blue-500 text-white',
};

const TONE_ICON: Record<ToastTone, IconName> = {
  success: 'check',
  error: 'cross-circle',
  info: 'info',
};

const DURATION_MS = 4000;

export const ToastProvider = (props: { children: JSX.Element }): JSX.Element => {
  const [toasts, setToasts] = createSignal<ToastMessage[]>([]);

  const remove = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const push = (tone: ToastTone, message: string) => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { id, tone, message }]);
    setTimeout(() => remove(id), DURATION_MS);
  };

  const api: ToastApi = {
    success: (m) => push('success', m),
    error: (m) => push('error', m),
    info: (m) => push('info', m),
  };

  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setToasts([]);
    };
    window.addEventListener('keydown', onKey);
    onCleanup(() => window.removeEventListener('keydown', onKey));
  });

  return (
    <ToastContext.Provider value={api}>
      {props.children}
      <Portal>
        <div class="fixed right-4 bottom-4 z-50 flex max-w-sm flex-col gap-2">
          <For each={toasts()}>
            {(t) => (
              <div
                class={cn(
                  'animate-slide-in flex items-center gap-3 rounded-lg px-4 py-3 text-sm shadow-lg',
                  TONE_CLASSES[t.tone],
                )}
                role="status"
                aria-live="polite"
              >
                <Icon name={TONE_ICON[t.tone]} class="h-5 w-5" />
                <span class="flex-1">{t.message}</span>
                <button
                  type="button"
                  class="rounded-full p-1 transition-colors hover:bg-white/20"
                  onClick={() => remove(t.id)}
                  aria-label="Dismiss"
                >
                  <Icon name="cross-small" />
                </button>
              </div>
            )}
          </For>
        </div>
      </Portal>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastApi => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
};
